/**
 * The asset catalog module (with `asset.view`): the catalog split by kind and by status, each legend entry opening
 * the list filtered to it, and how many assets are stale with a link to them. Status counts only what the viewer
 * may see (published only without `asset.view.draft`). It reads its own card, so a failure stays inside it.
 */

import { Link } from 'react-router-dom';
import { Library, TriangleAlert } from 'lucide-react';

import { useCatalogCard } from '@/api/queries';
import { ASSET_KINDS, ASSET_STATUSES, type CatalogCard } from '@/api/types';
import { badgeLabel } from '@/ui/Badge';
import { formatInt } from '@/ui/format';
import { SplitCard } from '@/ui/SplitCard';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { DashboardModule } from './DashboardModule';

const href = (key: string, value: string) => `/assets?${new URLSearchParams({ [key]: value }).toString()}`;

function StaleCard({ data }: { data: CatalogCard }) {
  return (
    <section className="metric-tile usage-card catalog-stale-card" aria-labelledby="catalog-stale-title">
      <h2 id="catalog-stale-title" className="dash-card-title">Freshness</h2>
      <p className="catalog-stale-figure">
        {data.stale > 0 && <TriangleAlert aria-hidden="true" />}
        <b>{formatInt(data.stale)}</b> {data.stale === 1 ? 'asset is stale' : 'assets are stale'}
      </p>
      <p className="hint">Stale: not confirmed as still accurate for more than six months.</p>
      <p className="dash-more">
        {data.stale > 0 ? <Link to="/assets?stale=true">Review the stale assets</Link> : <Link to="/assets">Open the catalog</Link>}
      </p>
    </section>
  );
}

function Cards({ data }: { data: CatalogCard }) {
  const statuses = ASSET_STATUSES.filter((s) => s in data.by_status);
  return (
    <div className="dash-grid catalog-dash-grid">
      <SplitCard
        label="Assets by kind"
        figure={data.total}
        noun={data.total === 1 ? 'asset in the catalog' : 'assets in the catalog'}
        aside={<Link className="usage-card-of" to="/assets">Open the catalog</Link>}
        parts={ASSET_KINDS.map((k, i) => ({ key: k, label: badgeLabel('kind', k), count: data.by_kind[k] ?? 0, seg: `seg-family-${i + 1}` }))}
        linkFor={(k) => href('kind', k)}
        linkTitle="Open in the catalog"
      />
      <SplitCard
        label="Assets by status"
        figure={statuses.reduce((sum, s) => sum + (data.by_status[s] ?? 0), 0)}
        noun="assets you can see"
        parts={statuses.map((s, i) => ({ key: s, label: badgeLabel('status', s), count: data.by_status[s] ?? 0, seg: `seg-family-${i + 1}` }))}
        linkFor={(s) => href('status', s)}
        linkTitle="Open in the catalog"
      />
      <StaleCard data={data} />
    </div>
  );
}

export function CatalogModule() {
  const card = useCatalogCard();
  let body;
  if (card.isPending) body = <LoadingCard label="Loading the catalog" />;
  else if (card.error) body = <LoadFailure error={card.error} onRetry={() => void card.refetch()} />;
  else if (card.data.total === 0 && Object.keys(card.data.by_status).every((s) => !card.data.by_status[s]))
    body = (
      <p className="hint catalog-dash-empty">
        <Library aria-hidden="true" /> The catalog is empty. <Link to="/assets">Open the catalog</Link>
      </p>
    );
  else body = <Cards data={card.data} />;
  return (
    <DashboardModule id="catalog" title="Asset catalog">
      {body}
    </DashboardModule>
  );
}
